import * as React from 'react';
import { flow, get, map } from 'lodash';
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { IMtaActions, IMtaFormNames, IMtaState, PageForm } from '../../../../../../../../services/mta/models';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { mtaActions } from '../../../../../../../../services/mta/actions';
import { actions as brandingActions } from '../../../../../../../../services/branding/reducer';
import { IBrandingActions } from '../../../../../../../../services/branding/models';
import { Button, Form } from 'react-bootstrap';
import { IOptionMap } from '../../../../../../../../data/api/models';
import { MTACardHeader } from '../Components/MTACardHeader';
import { MTAButtons } from '../../../MTAButtons';
import OptionWithPopup
  from '../../../../../../../Quote/scenes/AdditionalCover/components/QuestionOptions/OptionWithPopup';
import OptionsModal from '../Components/OptionsModal';
import { OptionsYesSelectionText } from '../Components/OptionsYesSelectionText';
import { alreadyAddedOptions, winterSportsRule } from '../../../MakeAChangeSelection/MTAHelpers';
import { LoadingSpinner } from '../../../../../../../../components/LoadingSpinner';
import { push } from 'react-router-redux';
import { moveValueToFront } from '../../../../../../../../helpers/ArrayHelpers';
import { differenceInYears } from 'date-fns';

interface IMTAAdditionalCoverFormProps extends InjectedFormProps<any> {
  options: IOptionMap;
  mta: IMtaState;
  formValues: any;
  visibility: { options: any };
  dispatch: any;
  actions: {
    branding: IBrandingActions;
    mta: IMtaActions;
    push: (route: string) => void;
  };
}

class MTAAdditionalCoverForm extends React.Component<IMTAAdditionalCoverFormProps, any> {

  constructor(props) {
    super(props);
    const { dispatch, change } = props;
    dispatch(change('page', PageForm.MTA_ADDITIONAL_COVER));
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.state = {
      isLoading: true,
      error: '',
      alreadyAddedOptions: [],
    };
  }

  public componentDidMount() {
    const { actions, dispatch, change, mta } = this.props;

    const changeForm = (key: string) => {
      dispatch(change(`data.attributes.metadata.options.${key}`, false));
      dispatch(change(`visibility.options.${key}`, false));
    }

    const addedOptions = alreadyAddedOptions(mta, changeForm)

    this.setState({ alreadyAddedOptions: addedOptions });

    actions.mta.calculateMtaPremiums().then(() => {
      this.setState({ isLoading: false });
    }).catch(() => {
      this.setState({ error: 'There has been a calculation error' });
    });
  }

  private renderOptions = (key, index) => {
    const { actions, visibility, mta, formValues } = this.props;
    const product = actions.branding.getProduct();
    const hasWSExclusion = get(mta, 'hasWinterSportsExclusion', false)
    const isInCoolingOff = mta?.policyInfo?.meta?.isInCoolingOff || false;
    const selectedOptionKey = mta?.selectedOptionToAdd;

    if (key === 'winter_sports' && hasWSExclusion) {
      return;
    }

    if (key === 'excess_waiver' && !isInCoolingOff) {
      return;
    }

    if (!['cruise_cover', 'winter_sports', 'gadget_cover', 'excess_waiver', 'enhanced_trip_disruption'].includes(key)) {
      return;
    }
    if (this.state.alreadyAddedOptions.includes(key)) {
      return;
    }

    if (get(formValues, 'metadata.scheme_type') === 'single') {
      if (!winterSportsRule(
        key,
        new Date(get(formValues, 'metadata.start_date')),
        new Date(get(formValues, 'metadata.end_date'))
      )) {
        return;
      }
    }

    const leadTraveller = formValues.metadata.adults[0];
    const leadTravellerAge = differenceInYears(new Date(), leadTraveller.dob);
    if (leadTravellerAge > 65 && key === 'winter_sports') {
      return null;
    }

    const selection = actions.mta.getSelectedScheme();
    const boltOns = get(selection, 'information.bolt_ons', null);
    const boltOn = get(boltOns, key, null);
    const boltOnTotal = get(boltOn, 'total', 0);
    const currency = get(selection, 'premium.currency', 'GBP');
    let dynamicText = product.metadata.definitions[`${key}_alt_dynamic`];
    let title;
    let yesSelection;
    let selectedNoText;

    if (!boltOnTotal) {
      return;
    }

    const isFullyDiscounted = selection.campaigns?.some(campaign => {
      return mta.selectedPolicy.campaigns?.some(policyCampaign => policyCampaign.id === campaign.id && policyCampaign.discount === 100)
    });

    if (key === 'gadget_cover') {
      title = 'Gadget Cover'
      yesSelection = [
        'Up to a maximum of £1,500 per trip for the loss, theft or damage of gadgets owned by yourself.',
        'Single item limit is up to £1,500',
        <>
          <div>
            <div>Refurbished gadgets must be bought directly from the manufacturer or a reputable retailer. For second
              hand items, you must be able to provide proof of purchase such as a printed or digital receipt.
            </div>
            <div style={{ marginTop: '10px' }}>Important: Gadgets are subject to wear and tear deductions. This doesn’t
              apply to
              gadgets less than 12 months old at the time of the incident. 10% per year up to a maximum depreciation of
              60%.
            </div>
            <div style={{ marginTop: '10px' }}>For example, if you bought an item for £200 three years ago, and the rate
              of
              depreciation is considered to be 10% a year, the deduction would be calculated as 30% (10% x 3 years) of
              the
              original £200.
            </div>
          </div>
        </>,
      ]
    }

    if (key === 'excess_waiver') {
      title = 'Excess Waiver'
      yesSelection = [
        'Excess waiver cover removes all excess amounts that are payable at the point a claim is made unless the reason for the claim occurred before the cover was active on your policy.',
      ]
    }

    if (key === 'winter_sports') {
      title = 'Winter Sports'
      yesSelection = [
        'Up to £500 for winter sports related incidents',
        'Lost, stolen or damaged equipment owned or hired by yourself',
        'Piste closure and delay caused by avalanche',
        'Winter Sports trips are covered up to a maximum of 21 days per policy term'
      ]

      selectedNoText = (
        <div>IMPORTANT - This policy will not cover claims for medical treatment, cancellation or cutting your trip
          short if you take part in winter sports activities.
        </div>
      )
    }

    if (key === 'cruise_cover') {
      title = 'Cruise Cover'
      yesSelection = [
        'Up to £750 for Cruise related incidents',
        'Missed port departure, cruise interruption and itinerary change',
        'Extend your policy limits to cover you whilst on a cruise',
      ]

      selectedNoText = (
        <div>IMPORTANT - This policy will not cover claims for medical treatment, cancellation or cutting your trip
          short if you plan on going on a cruise.
        </div>
      )
    }

    if (key === 'enhanced_trip_disruption') {
      title = 'Enhanced Trip Disruption Cover'
      yesSelection = [
        'Cover for the delay benefit with the minimum delay period to qualify for cover reduced from 12 hours to 6 hours',
        'Cover for onward expenses incurred as the result of a delay, such as additional transfer/car hire costs as well as cover for expenses for unused excursions ',
        'Cover for disruption caused by airspace closure, air traffic control restrictions, mid-air emergencies, and technical issues',
        'Cover for connecting flights (outbound and return journeys only)',
      ]

      selectedNoText = (
        <div>
          <b>Please note - </b>
          The enhanced trip disruption cover does not cover mid-trip connections or unused accommodation for delays of
          less than 24 hours
        </div>
      )
    }

    dynamicText = (<OptionsYesSelectionText text={yesSelection} optionKey={key}/>)

    return (
      <>
        <div className="admiral-card" style={{ maxWidth: '950px', margin: 'auto auto 15px auto' }}>
          <div className="body" style={{ padding: '15px' }}>
            <div className="medical-section-group options-section-group" key={key}>

              <OptionWithPopup title={title}
                               name={`data.attributes.metadata.options.${key}`}
                               visibilityName={`options.${key}`}
                               selectedYesText={dynamicText}
                               selectedNoText={selectedNoText}
                               optionKeyName={key}
                               onOptionClick={this.recalculatePrice}
                               disabledCase={mta.isLoading}
                               currency={currency}
                               boltOnTotal={boltOnTotal}
                               discountedPrice={isFullyDiscounted ? 0.00 : null}
                               active={visibility && visibility?.options[`${key}`]}
                               visibilityOfAddButton={visibility && visibility.options[key]}
                               props={this.props}
                               unselectAdd={this.unselectAdd.bind(this, key)}/>
            </div>
          </div>
        </div>
        {index === 0 && selectedOptionKey && (
          <div className="admiral-card" style={{ maxWidth: '950px', margin: 'auto auto 15px auto', padding: '15px' }}>
            <div className={'info-options-box-blue-container'}>
              <div className={'icon'}><img src={require('../../../../Images/question.svg')} width={'50px'} alt={''}/>
              </div>
              <div className={'text'}>
                <div>Other additional cover options available to you</div>
              </div>
            </div>
          </div>
        )}
      </>
    );

  }

  private unselectAdd = (key) => {
    const { options } = this.props;
    if (options[key]) {
      this.props.change(`data.attributes.metadata.options.${key}`, false);
      this.recalculatePrice();
    }
  }

  private recalculatePrice = () => {
    const { actions } = this.props;
    setTimeout((() => {
      actions.mta.calculateMtaPremiums().then(() => {
        actions.mta.addEndorsements();
      });
    }), 300);
  }

  public handleSubmit = () => {
    const { actions } = this.props;
    actions.mta.handleMTASubmit();
  }

  private isSubmitButtonDisabled = () => {
    const { mta, submitting, submitFailed, visibility, options, valid } = this.props;
    return submitting ||
      (submitFailed && !valid) ||
      (visibility && visibility.options.winter_sports === false) &&
      (visibility && visibility.options.cruise_cover === false) &&
      (visibility && visibility.options.golf_cover === false) &&
      (visibility && visibility.options.gadget_cover === false) &&
      (visibility && visibility.options.excess_waiver === false) ||
      (visibility && visibility.options.winter_sports === true && options.winter_sports === false) ||
      (visibility && visibility.options.cruise_cover === true && options.cruise_cover === false) ||
      (visibility && visibility.options.golf_cover === true && options.golf_cover === false) ||
      (visibility && visibility.options.gadget_cover === true && options.gadget_cover === false) ||
      (visibility && visibility.options.excess_waiver === true && options.excess_waiver === false) ||
      mta.isLoading ||
      !valid;
  }

  private handleGoBack = () => {
    const { actions } = this.props;
    actions.mta.setCompleteMTA();
    actions.push(`/account/summary`)
  }

  public render() {
    const { handleSubmit, mta } = this.props;
    const { isLoading, error } = this.state;
    const selectedOptionKey = mta?.selectedOptionToAdd;
    const optionKeys = ['winter_sports', 'cruise_cover', 'gadget_cover', 'excess_waiver', 'enhanced_trip_disruption'];
    let sortedResult = optionKeys;

    if (selectedOptionKey) {
      sortedResult = moveValueToFront(optionKeys, selectedOptionKey);
    }

    const submitButton = (
      <Button
        id="continue"
        bsStyle="primary"
        type="submit"
        className="pull-right medium-width secondary-button"
        disabled={this.isSubmitButtonDisabled()}
      >
        APPLY CHANGES
      </Button>
    );

    const goBackButton = (
      <Button
        bsStyle={'primary'}
        onClick={this.handleGoBack}
        className={'medium-width secondary-button'}>
        GO BACK
      </Button>
    )

    if (isLoading) {
      return (
        <LoadingSpinner text={'Getting your options...'}/>
      )
    }

    return (
      <div className="page-container">
        {error && (
          <div className="error-block-container">
            <h1>Whoops!</h1>
            <p>{error}</p>
          </div>
        )}

        {isLoading && (
          <LoadingSpinner text={'Getting your options...'}/>
        )}

        <Form horizontal={true} onSubmit={handleSubmit(this.handleSubmit)} autoComplete="off">

          <div style={{ maxWidth: '950px', margin: '20px auto 20px auto' }}>
            <MTACardHeader
              text={'Personalise your cover below.'}
              subtext={'Please answer the following questions to make sure your cover meets your needs. '}
              icon={'insurance'}
            />
          </div>

          <div>
            <div>
              {map(sortedResult, this.renderOptions)}
            </div>
          </div>

          <OptionsModal/>

          <div style={{ maxWidth: '950px', margin: '20px auto 20px auto' }}>
            <MTAButtons button1={goBackButton} button2={submitButton}/>
          </div>

        </Form>

      </div>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: IMtaFormNames.COVER_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(IMtaFormNames.COVER_DETAILS);
    return component;
  },
  connect(
    (state: IStore) => {
      const options = selector(state, 'data.attributes.metadata.options');
      const visibility = selector(state, 'visibility');
      const values = selector(
        state,
        'data.attributes',
      );
      return {
        mta: state.mta,
        options,
        visibility,
        formValues: values,
      };
    },
    (dispatch) => ({
      actions: {
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(MTAAdditionalCoverForm);
